import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { TestimonialService } from 'src/app/services/testimonial/testimonial.service';
import { TestimonialModalService } from 'src/app/services/testimonial-modal/testimonial-modal.service';
import { ClientService } from 'src/app/services/user/client.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-testimonial',
  templateUrl: './add-edit-testimonial.component.html',
  styleUrls: ['./add-edit-testimonial.component.scss']
})
export class AddEditTestimonialComponent extends DestructorComponent implements OnInit {

  @Output() testimonialAdded = new EventEmitter<void>();
  @Output() testimonialUpdated = new EventEmitter<void>();

  testimonialForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedTestimonial: any = null;

  // User search properties
  userSearchTerm: string = '';
  selectedUser: any = null;
  userSearchResults: any[] = [];
  showUserDropdown: boolean = false;
  isUserSearching: boolean = false;
  private userSearchSubject = new Subject<string>();

  // Rating options
  ratingOptions = [1, 2, 3, 4, 5];

  constructor(
    private fb: FormBuilder,
    private _testimonialService: TestimonialService,
    private _testimonialModalService: TestimonialModalService,
    private _clientService: ClientService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.setupUserSearchDebounce();
  }

  private initializeForm(): void {
    this.testimonialForm = this.fb.group({
      message: ['', [Validators.required, Validators.minLength(10)]],
      rating: [5, [Validators.required, Validators.min(1), Validators.max(5)]]
    });
  }

  // Getter methods for form controls
  get message() { return this.testimonialForm.get('message'); }
  get rating() { return this.testimonialForm.get('rating'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_testimonial_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceTestimonial = this._testimonialModalService.getSelectedTestimonial();
        if (serviceTestimonial) {
          this.isEditMode = true;
          this.selectedTestimonial = serviceTestimonial;
          this.populateForm(this.selectedTestimonial);
        } else {
          this.isEditMode = false;
          this.selectedTestimonial = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._testimonialModalService.clearSelectedTestimonial();
        this.resetForm();
      });
    }
  }

  private populateForm(testimonial: any): void {
    this.testimonialForm.patchValue({
      message: testimonial.message || '',
      rating: testimonial.rating || 5
    });

    // Set selected user for edit mode
    if (testimonial.name && testimonial.country) {
      this.selectedUser = {
        name: testimonial.name,
        country_obj: {
          name: testimonial.country
        }
      };
      this.userSearchTerm = testimonial.name;
    }
  }

  private resetForm(): void {
    this.testimonialForm.reset({
      message: '',
      rating: 5
    });
    this.selectedUser = null;
    this.userSearchTerm = '';
    this.userSearchResults = [];
    this.showUserDropdown = false;
    this.isEditMode = false;
    this.selectedTestimonial = null;
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_testimonial_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // User search methods
  private setupUserSearchDebounce(): void {
    this.userSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchUsers(searchTerm);
    });
  }

  onUserSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.userSearchTerm = target.value;
    this.selectedUser = null;
    this.userSearchSubject.next(this.userSearchTerm);
    this.showUserDropdown = true;
  }

  onUserInputFocus(): void {
    if (this.userSearchResults.length > 0) {
      this.showUserDropdown = true;
    }
  }

  onUserInputBlur(): void {
    setTimeout(() => {
      this.showUserDropdown = false;
    }, 200);
  }

  private searchUsers(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.userSearchResults = [];
      this.showUserDropdown = false;
      return;
    }

    this.isUserSearching = true;
    this.subscriptions.push(
      this._clientService.GetData('1', searchTerm).subscribe({
        next: (response: any) => {
          this.isUserSearching = false;
          if (response.status && response.data && response.data.data) {
            this.userSearchResults = response.data.data;
            this.showUserDropdown = this.userSearchResults.length > 0;
          } else {
            this.userSearchResults = [];
            this.showUserDropdown = false;
          }
        },
        error: (error: any) => {
          this.isUserSearching = false;
          console.error('Error searching users:', error);
          this.userSearchResults = [];
          this.showUserDropdown = false;
        }
      })
    );
  }

  selectUser(user: any): void {
    this.selectedUser = user;
    this.userSearchTerm = user.name;
    this.showUserDropdown = false;
  }

  clearUserSelection(): void {
    this.selectedUser = null;
    this.userSearchTerm = '';
    this.userSearchResults = [];
  }

  onSubmit(): void {
    if (!this.selectedUser && !this.isEditMode) {
      Swal.fire({
        text: "Please select a user!",
        icon: "warning",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
      return;
    }

    if (this.testimonialForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.testimonialForm.value;

      // Prepare submit data
      const submitData: any = {
        message: formData.message,
        rating: formData.rating
      };

      // Add user data only if user is selected (for add mode or if changed in edit mode)
      if (this.selectedUser) {
        submitData.name = this.selectedUser.name;
        submitData.country = this.selectedUser.country_obj?.name || '';
      }

      const apiCall = this.isEditMode
        ? this._testimonialService.UpdateData(this.selectedTestimonial._id, submitData)
        : this._testimonialService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Testimonial operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Testimonial has been successfully updated!"
                : "Testimonial has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.testimonialUpdated.emit();
                } else {
                  this.testimonialAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Error saving testimonial:', error);
            Swal.fire({
              text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.testimonialForm.controls).forEach(key => {
        this.testimonialForm.get(key)?.markAsTouched();
      });
    }
  }
}
